import { useController } from 'react-hook-form'

import { Flex, Slider, TextField } from '@radix-ui/themes'

import { UnselectableText } from '../../styled/text'
import { TCommonFields } from '../../types'
import { FieldLabel, FieldRow, getFieldRules } from '../field-row'
import { useFieldState } from '../hooks'

export type TSliderFields<T> = TCommonFields<T> & {
    type: 'slider'
    min: number
    max: number
    inputMax?: number
}

type TProps<T> = {
    rowField: TSliderFields<T>
    fieldName: string
    labelWidth?: string
}

const SliderField = <T,>({ fieldName, rowField, labelWidth }: TProps<T>) => {
    const { disabled, required } = useFieldState(rowField)

    const {
        field: { onChange, value, disabled: _disabled, ...rest },
    } = useController({
        name: fieldName,
        rules: getFieldRules(rowField, required),
        defaultValue: '',
    })

    const change = (value: number | '') => {
        onChange(value)
        rowField.onChange?.(value)
    }

    return (
        <FieldRow
            fieldName={fieldName}
            label={rowField.label}
            required={required}
            labelWidth={labelWidth}
            className="form-slider"
            hideLabel
        >
            <Flex mb="1" align="center">
                <FieldLabel htmlFor={fieldName} label={rowField.label} required={required} />
                <TextField.Root
                    id={fieldName}
                    value={value}
                    disabled={disabled}
                    onChange={e => {
                        let value: number | '' = e.target.value !== '' ? +e.target.value : ''
                        if (
                            rowField.inputMax !== undefined &&
                            typeof value == 'number' &&
                            rowField.inputMax < value
                        )
                            value = rowField.inputMax

                        change(value)
                    }}
                    type="number"
                />
            </Flex>

            <Slider
                variant="soft"
                color="iris"
                radius="full"
                min={rowField.min}
                max={rowField.max}
                {...rest}
                disabled={disabled}
                onValueChange={([value]) => change(value)}
                value={[value]}
            />

            <Flex justify="between" mt="2" mx="1" height="0" pb="3" width="100%">
                <UnselectableText size="1" color="gray">
                    {rowField.min}
                </UnselectableText>
                <UnselectableText size="1" color="gray">
                    {rowField.max}
                </UnselectableText>
            </Flex>
        </FieldRow>
    )
}

export default SliderField
